import React from "react";
import { MobXProviderContext, observer } from "mobx-react";

// Sagres Rotas React example components
import { Card, CardContent, Grid, Typography, TextField, Button, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Header from "components/Header/Header";

// HERE Maps
import H from "@here/maps-api-for-javascript";
import onResize from "simple-element-resize-detector";
import { getNextApiKey } from "../../config/hereConfig";

// Chart.js
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function RelatoriosView() {
  const { authStore, relatoriosStore, entregadoresStore, mapaStore } = React.useContext(MobXProviderContext);
  const navigate = useNavigate();

  // Map ref and state
  const mapRef = React.createRef();
  const [heatmapInstance, setHeatmapInstance] = React.useState(null);

  // Initialize dates - default to current month
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const formatDateToInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateToAPI = (dateString) => {
    // Convert YYYY-MM-DD to YYYYMMDD
    return dateString.replace(/-/g, '');
  };

  const [dataInicial, setDataInicial] = React.useState(formatDateToInput(firstDayOfMonth));
  const [dataFinal, setDataFinal] = React.useState(formatDateToInput(lastDayOfMonth));

  const handleBuscarRelatorio = () => {
    const dataInicialAPI = formatDateToAPI(dataInicial);
    const dataFinalAPI = formatDateToAPI(dataFinal);
    relatoriosStore.buscarDashboard(dataInicialAPI, dataFinalAPI);
  };

  var height = window.innerHeight;
  const dashboard = relatoriosStore.dashboard;

  // Fetch data on mount
  React.useEffect(() => {
    handleBuscarRelatorio();
    // Fetch entregadores for name lookup in charts
    if (entregadoresStore.entregadores.length === 0) {
      entregadoresStore.buscarEntregadores();
    }
  }, []);

  // Initialize heatmap
  React.useEffect(() => {
    if (!mapRef.current || heatmapInstance) return;

    const platform = new H.service.Platform({
      apiKey: getNextApiKey()
    });

    const layers = platform.createDefaultLayers();
    const map = new H.Map(
      mapRef.current,
      layers.vector.normal.map,
      {
        pixelRatio: window.devicePixelRatio,
        center: { lat: mapaStore.origemRestaurante.lat, lng: mapaStore.origemRestaurante.lng },
        zoom: 13,
      }
    );

    new H.mapevents.Behavior(new H.mapevents.MapEvents(map));
    H.ui.UI.createDefault(map, layers, 'pt-BR');

    onResize(mapRef.current, () => {
      map.getViewPort().resize();
    });

    setHeatmapInstance(map);
  }, [dashboard]);

  // Update heatmap markers when dashboard data changes
  React.useEffect(() => {
    if (!heatmapInstance || !dashboard || !dashboard.Heatmap) return;

    // Clear existing heatmap markers
    heatmapInstance.removeObjects(heatmapInstance.getObjects().filter(obj => obj.getData() === 'heatmap'));

    // Create red circular markers with opacity
    dashboard.Heatmap.forEach(point => {
      if (point.Latitude && point.Longitude) {
        // Create SVG for circular red marker with opacity
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30">
          <circle cx="15" cy="15" r="12" fill="rgba(255, 0, 0, 0.3)"/>
        </svg>`;

        const icon = new H.map.Icon(svg);
        const marker = new H.map.Marker(
          { lat: point.Latitude, lng: point.Longitude },
          {
            icon: icon,
            data: 'heatmap'
          }
        );

        heatmapInstance.addObject(marker);
      }
    });

    // Adjust map view to show all markers if there are any
    if (dashboard.Heatmap.length > 0) {
      const validPoints = dashboard.Heatmap.filter(p => p.Latitude && p.Longitude);
      if (validPoints.length > 0) {
        const bounds = new H.geo.Rect(
          Math.max(...validPoints.map(p => p.Latitude)),
          Math.min(...validPoints.map(p => p.Longitude)),
          Math.min(...validPoints.map(p => p.Latitude)),
          Math.max(...validPoints.map(p => p.Longitude))
        );
        heatmapInstance.getViewModel().setLookAtData({ bounds: bounds });
      }
    }
  }, [dashboard, heatmapInstance]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div>
      <Header />

      <div style={{ position: 'relative', width: '100%', height: `${height - 5}px`, overflowY: 'auto' }}>

        <Card style={{ margin: '32px', padding: 0, }}>
          <CardContent style={{ padding: 0 }}>
            <Typography gutterBottom variant="h5" component="div" style={{ marginLeft: 5, paddingTop: 16, paddingLeft: 8 }}>
              Relatórios
            </Typography>

            <Grid container direction="column" spacing={3} padding={3}>
              {/* Date Pickers */}
              <Grid item>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <TextField
                      label="Data Inicial"
                      type="date"
                      value={dataInicial}
                      onChange={(e) => setDataInicial(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      label="Data Final"
                      type="date"
                      value={dataFinal}
                      onChange={(e) => setDataFinal(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleBuscarRelatorio}
                      disabled={relatoriosStore.inProgress}
                    >
                      Buscar
                    </Button>
                  </Grid>
                </Grid>
              </Grid>

              {/* Summary Cards */}
              {dashboard && (
                <Grid item>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Card sx={{ backgroundColor: '#f5f5f5' }}>
                        <CardContent>
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            Número de Pedidos
                          </Typography>
                          <Typography variant="h3" color="primary" fontWeight="bold">
                            {dashboard.NumPedidos}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ backgroundColor: '#f5f5f5' }}>
                        <CardContent>
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            Faturamento
                          </Typography>
                          <Typography variant="h3" color="success.main" fontWeight="bold">
                            {formatCurrency(dashboard.Faturamento)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ backgroundColor: '#f5f5f5' }}>
                        <CardContent>
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            Ticket Médio
                          </Typography>
                          <Typography variant="h3" color="info.main" fontWeight="bold">
                            {formatCurrency(dashboard.TicketMedio)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {/* Line Chart - Pedidos por Dia */}
              {dashboard && dashboard.PedidosPorDia && dashboard.PedidosPorDia.length > 0 && (
                <Grid item>
                  <Card sx={{ backgroundColor: '#ffffff' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Pedidos por Dia
                      </Typography>
                      <Box sx={{ height: 400 }}>
                        <Line
                          data={{
                            labels: dashboard.PedidosPorDia.map(item => {
                              // Format date from YYYY-MM-DD to DD/MM
                              const [year, month, day] = item.Data.split('-');
                              return `${day}/${month}`;
                            }),
                            datasets: [
                              {
                                label: 'Pedidos',
                                data: dashboard.PedidosPorDia.map(item => item.Pedidos),
                                borderColor: 'rgb(75, 192, 192)',
                                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                                tension: 0.3,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'top',
                              },
                              title: {
                                display: false,
                              },
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  precision: 0,
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Horizontal Bar Charts Row */}
              {dashboard && (
                <Grid item>
                  <Grid container spacing={3}>
                    {/* TopBairros Chart */}
                    {dashboard.TopBairros && dashboard.TopBairros.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card sx={{ backgroundColor: '#ffffff' }}>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Bairros com Mais Pedidos
                            </Typography>
                            <Box sx={{ height: 400 }}>
                              <Bar
                                data={{
                                  labels: dashboard.TopBairros.map(item => item.Bairro),
                                  datasets: [
                                    {
                                      label: 'Pedidos',
                                      data: dashboard.TopBairros.map(item => item.Pedidos),
                                      backgroundColor: 'rgba(54, 162, 235, 0.5)',
                                      borderColor: 'rgb(54, 162, 235)',
                                      borderWidth: 1,
                                    },
                                  ],
                                }}
                                options={{
                                  indexAxis: 'y',
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: {
                                    legend: {
                                      display: false,
                                    },
                                    title: {
                                      display: false,
                                    },
                                  },
                                  scales: {
                                    x: {
                                      beginAtZero: true,
                                      ticks: {
                                        precision: 0,
                                      },
                                    },
                                  },
                                }}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {/* PorEntregador Chart */}
                    {dashboard.PorEntregador && dashboard.PorEntregador.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card sx={{ backgroundColor: '#ffffff' }}>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Pedidos por Entregador
                            </Typography>
                            <Box sx={{ height: 400 }}>
                              <Bar
                                data={{
                                  labels: dashboard.PorEntregador.map(item => {
                                    // Find entregador name from store
                                    const entregador = entregadoresStore.entregadores?.find(e => e.EntregadorId === item.EntregadorId);
                                    return entregador?.Nome || `Entregador ${item.EntregadorId}`;
                                  }),
                                  datasets: [
                                    {
                                      label: 'Pedidos',
                                      data: dashboard.PorEntregador.map(item => item.Pedidos),
                                      backgroundColor: 'rgba(255, 159, 64, 0.5)',
                                      borderColor: 'rgb(255, 159, 64)',
                                      borderWidth: 1,
                                    },
                                  ],
                                }}
                                options={{
                                  indexAxis: 'y',
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: {
                                    legend: {
                                      display: false,
                                    },
                                    title: {
                                      display: false,
                                    },
                                  },
                                  scales: {
                                    x: {
                                      beginAtZero: true,
                                      ticks: {
                                        precision: 0,
                                      },
                                    },
                                  },
                                }}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              {/* Heatmap */}
              {dashboard && (
                <Grid item>
                  <Card sx={{ backgroundColor: '#ffffff' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Mapa de Calor - Entregas
                      </Typography>
                      {dashboard.Heatmap && dashboard.Heatmap.length > 0 ? (
                        <Box
                          ref={mapRef}
                          sx={{
                            height: 500,
                            width: '100%',
                            position: 'relative'
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          Nenhum dado de localização disponível para o período selecionado.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {relatoriosStore.inProgress && (
                <Grid item>
                  <Typography variant="body1" color="text.secondary">
                    Carregando relatório...
                  </Typography>
                </Grid>
              )}

              {relatoriosStore.error && (
                <Grid item>
                  <Typography variant="body1" color="error">
                    Erro: {relatoriosStore.error}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </div>

    </div>
  );
}

export default observer(RelatoriosView);
